import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import AdminNavBar from '../components/AdminNavBar';
import { useAuth } from '../hooks/useAuth';
import { Database } from '../types/supabase';
import ReactMarkdown from 'react-markdown';
// @ts-ignore
import remarkGfm from 'remark-gfm';

type LLMResponse = Database['public']['Tables']['llm_responses']['Row'];
type LLMOutputCombined = Database['public']['Tables']['llm_output_combined']['Row'];
type Submission = Database['public']['Tables']['submissions']['Row'];

interface LLMOutput {
  id: string;
  response_text: string | null;
  type: 'llm_response' | 'llm_output_combined';
  model?: string;
  combiner_model?: string;
  // Present only for llm_responses
  prompt_path?: string | null;
  status: string;
  created_at: string;
  completed_at: string | null;
}

const LLMOutputsDisplay: React.FC = () => {
  const { submissionID } = useParams<{ submissionID: string }>();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const supabase = useSupabase();

  const [outputs, setOutputs] = useState<LLMOutput[]>([]);
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOutputs, setExpandedOutputs] = useState<Set<string>>(new Set());

  const fetchLLMOutputs = useCallback(async () => {
    if (!submissionID) {
      setError('No submission ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch submission details
      const { data: submissionData, error: submissionError } = await supabase
        .from('submissions')
        .select('*')
        .eq('id', submissionID)
        .single();

      if (submissionError) {
        throw submissionError;
      }

      setSubmission(submissionData);

      // Fetch from llm_responses table
      const { data: llmResponses, error: responsesError } = await supabase
        .from('llm_responses')
        .select('*')
        .eq('submission_id', submissionID);

      if (responsesError) {
        throw responsesError;
      }

      // Fetch from llm_output_combined table
      const { data: llmOutputsCombined, error: combinedError } = await supabase
        .from('llm_output_combined')
        .select('*')
        .eq('submission_id', submissionID);

      if (combinedError) {
        throw combinedError;
      }

      // Transform and combine the data
      const transformedResponses: LLMOutput[] = (llmResponses || []).map((response: LLMResponse) => ({
        id: response.id,
        response_text: response.response_text,
        type: 'llm_response' as const,
        model: response.model,
        prompt_path: response.prompt_path,
        status: response.status,
        created_at: response.queued_at,
        completed_at: response.completed_at,
      }));

      const transformedCombined: LLMOutput[] = (llmOutputsCombined || []).map((combined: LLMOutputCombined) => ({
        id: combined.id,
        response_text: combined.response_text,
        type: 'llm_output_combined' as const,
        combiner_model: combined.combiner_model,
        status: combined.status,
        created_at: combined.created_at,
        completed_at: combined.completed_at,
      }));

      // Combine and sort by priority, then by creation date
      // Priority order:
      // 1) llm_responses with prompt_path including "needs_action"
      // 2) llm_output_combined
      // 3) remaining llm_responses (draft)
      const priorityOf = (o: LLMOutput) => {
        const isNeedsAction = o.type === 'llm_response' && (o.prompt_path || '').toLowerCase().includes('needs_action');
        if (isNeedsAction) return 1;
        if (o.type === 'llm_output_combined') return 2;
        if (o.type === 'llm_response') return 3;
        return 99;
      };

      const allOutputs = [...transformedResponses, ...transformedCombined].sort((a, b) => {
        const pa = priorityOf(a);
        const pb = priorityOf(b);
        if (pa !== pb) return pa - pb;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });

      setOutputs(allOutputs);
    } catch (err) {
      console.error('Error fetching LLM outputs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch LLM outputs');
    } finally {
      setLoading(false);
    }
  }, [submissionID, supabase]);

  useEffect(() => {
    if (!submissionID) {
      setError('No submission ID provided');
      setLoading(false);
      return;
    }

    fetchLLMOutputs();
  }, [fetchLLMOutputs, submissionID]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  const toggleExpanded = (outputId: string) => {
    const newExpanded = new Set(expandedOutputs);
    if (newExpanded.has(outputId)) {
      newExpanded.delete(outputId);
    } else {
      newExpanded.add(outputId);
    }
    setExpandedOutputs(newExpanded);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'pending':
      case 'queued':
        return 'bg-yellow-100 text-yellow-800';
      case 'running':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const truncateText = (text: string, maxLength: number = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  // Combined outputs are rendered as Markdown; legacy JSON parser/renderer removed.

  if (loading) {
    return (
      <>
        <AdminNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16 max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F55D3E]"></div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AdminNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16 max-w-7xl mx-auto">
          <div className="bg-red-50 border-l-4 border-red-500 p-4">
            <p className="text-red-700">Error: {error}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavBar user={user} onSignOut={handleSignOut} />
      <div className="p-8 mt-16 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#343a40]">LLM Outputs</h1>
            <h2 className="text-2xl font-bold text-[#343a40] mt-2">
              {submission?.data && typeof submission.data === 'object' && 'protocol_title' in submission.data
                ? String(submission.data.protocol_title)
                : 'LLM Outputs'}
            </h2>
            <h3 className="text-xl text-[#343a40] mt-1">
              Sponsor: {submission?.data && typeof submission.data === 'object' && 'sponsor_name' in submission.data
                ? String(submission.data.sponsor_name)
                : ''}
            </h3>
            <h4 className="text-lg text-gray-600">
              PI: {submission?.data && typeof submission.data === 'object' && 'pi_first_name' in submission.data
                ? String(submission.data.pi_first_name)
                : ''} {submission?.data && typeof submission.data === 'object' && 'pi_last_name' in submission.data
                  ? String(submission.data.pi_last_name)
                  : ''}
            </h4>
            <p className="text-lg text-gray-600 mt-1">Submission ID: {submissionID}</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
          >
            Back
          </button>
        </div>

        {outputs.length === 0 ? (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
            <p className="text-yellow-700">No LLM outputs found for this submission.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {outputs.map((output) => (
              <div key={output.id} className="bg-white rounded-lg shadow-md border border-gray-200">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          output.type === 'llm_response' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {output.type === 'llm_response' ? 'LLM Response' : 'Combined Output'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(output.status)}`}>
                          {output.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p><strong>Model:</strong> {output.model || output.combiner_model || 'N/A'}</p>
                        <p><strong>Created:</strong> {formatDate(output.created_at)}</p>
                        {output.completed_at && (
                          <p><strong>Completed:</strong> {formatDate(output.completed_at)}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {output.response_text ? (
                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-[#343a40]">Response Text</h3>
                          {output.type === 'llm_response' && (
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                ((output.prompt_path || '').toLowerCase().includes('needs_action'))
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {((output.prompt_path || '').toLowerCase().includes('needs_action'))
                                ? 'needs_action to irb_review'
                                : 'draft to irb_review'}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => toggleExpanded(output.id)}
                          className="px-3 py-1 text-sm bg-[#F55D3E] text-white rounded-md hover:bg-[#E54D2E] transition-colors"
                        >
                          {expandedOutputs.has(output.id) ? 'Collapse' : 'Expand'}
                        </button>
                      </div>
                      <div className="bg-gray-50 rounded-md p-4 max-h-96 overflow-y-auto">
                        {output.type === 'llm_response' ? (
                          <div className="prose prose-sm max-w-none text-gray-800 prose-table:table-auto prose-th:bg-gray-100 prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-td:border prose-th:border">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {expandedOutputs.has(output.id) 
                                ? output.response_text || ''
                                : truncateText(output.response_text || '')
                              }
                            </ReactMarkdown>
                          </div>
                        ) : (
                          <div className="prose prose-sm max-w-none text-gray-800 prose-table:table-auto prose-th:bg-gray-100 prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-td:border prose-th:border">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {expandedOutputs.has(output.id)
                                ? output.response_text || ''
                                : truncateText(output.response_text || '')}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="border-t pt-4">
                      <p className="text-gray-500 italic">No response text available</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default LLMOutputsDisplay;
